import React, { createRef } from 'react';
import Dropzone, { DropzoneInputProps, DropzoneRef, DropzoneRootProps, FileRejection } from 'react-dropzone';
import { Prompt } from 'react-router';
import {
  ButtonWithSpinner,
  Card,
  Upload,
  UploadCta,
} from '@amount/frontend-components';
import SessionManager from '../../../util/sessionManager';
import FileList from '../../VerDash/FileUpload/Uploading';
import { BadFileType, UploadFailure } from '../../VerDash/FileUpload/Presentational/ErrorModals';
import {
  ACCEPT_MIMES,
  Desktop,
  DropzoneActiveClass,
  IFileWithPreview,
  IUploadFileSuccessResponse,
  Mobile,
  uploadFile,
  UploadFileResponse,
  wasSuccessfulUpload,
  WithFileName,
  FileListDropEnabledUpload
} from '../../VerDash/FileUpload/common';

export interface IDropForCustomerState {
  preview?: string;
  previews: Map<string, IFileWithPreview>;
  errorPreviews?: FileRejection[];
  uploading: boolean;
  showUploadErrorModal: boolean;
  showInvalidTypeModal: boolean;
  submitError?: string;
}

interface IDropForCustomerProps {
  customerUUID: string;
  uploadType: string;
  onSuccess?(): void;
}

interface IPreDropUploadProps {
  uploadButtons: React.RefObject<HTMLDivElement>;
  getRootProps(props?: DropzoneRootProps): DropzoneRootProps;
  openFileDialog(event: React.MouseEvent<HTMLElement, MouseEvent>): void;
  getInputProps(props?: DropzoneInputProps): DropzoneInputProps;
}

interface IPostDropUpload extends IPreDropUploadProps {
  previews: Map<string, IFileWithPreview>;
  removeImageHtml(event: React.MouseEvent<HTMLElement, MouseEvent>): void;
}

export const PreDropUpload: React.FC<IPreDropUploadProps> = ({
  getRootProps,
  getInputProps,
  uploadButtons,
  openFileDialog
}) => (
  <Upload
    data-test-id='file-upload'
    {...getRootProps()}
    activeClassName={DropzoneActiveClass}
    ref={uploadButtons}
  >
    <input {...getInputProps()} />
    <Mobile openFileDialog={openFileDialog} />
    <Desktop openFileDialog={openFileDialog} />
  </Upload>
);

export const PostDropUpload: React.FC<IPostDropUpload> = ({
  getRootProps,
  getInputProps,
  uploadButtons,
  openFileDialog,
  previews,
  removeImageHtml
}) => (
  <FileListDropEnabledUpload
    data-test-id='file-upload'
    {...getRootProps()}
    activeClassName={DropzoneActiveClass}
    ref={uploadButtons}
  >
    <Card padding='1.75em 1.5em'>
      <input {...getInputProps()} />
      <FileList
        legacyUploadTask={false}
        files={previews}
        removeImage={removeImageHtml}
        openFileDialog={openFileDialog}
      />
    </Card>
  </FileListDropEnabledUpload>
);

class DropForCustomer extends React.Component<IDropForCustomerProps, IDropForCustomerState> {
  public state: Readonly<IDropForCustomerState> = {
    previews: new Map<string, IFileWithPreview>(),
    uploading: false,
    showUploadErrorModal: false,
    showInvalidTypeModal: false,
  };

  public _dropzone: React.RefObject<DropzoneRef>;
  public _uploadButtons: React.RefObject<HTMLDivElement>;

  constructor (props: IDropForCustomerProps) {
    super(props);

    this._uploadButtons = React.createRef();
    this._dropzone = createRef<DropzoneRef>();
  }

  public isPreDropUpload = () => this.state.previews.size === 0;

  public componentWillUnmount () {
    [...this.state.previews.values()].forEach(file => {
      if (file.preview) {
        window.URL.revokeObjectURL(file.preview);
      }
    });
  }

  public render () {
    return (
      <div>
        <BadFileType
          show={this.state.showInvalidTypeModal}
          close={this.closeInvalidTypeModal}
        />
        <UploadFailure
          show={this.state.showUploadErrorModal}
          close={this.closeUploadErrorModal}
        />
        <Prompt
          message='Are you sure you want to leave without submitting your documents?'
          when={this.state.previews.size > 0 && !this.state.uploading}
        />
        <Dropzone
          ref={this._dropzone}
          onDrop={this.handleDrop}
          accept={ACCEPT_MIMES}
          multiple={true}
          onDropRejected={this.handleDropFailure}
          noClick={!this.isPreDropUpload()}
        >
          {({ getRootProps, getInputProps }) => (
            this.isPreDropUpload() ? (
              <PreDropUpload
                getInputProps={getInputProps}
                getRootProps={getRootProps}
                openFileDialog={this.openFileDialog}
                uploadButtons={this._uploadButtons}
              />
            ) : (
              <PostDropUpload
                previews={this.state.previews}
                removeImageHtml={this.removeImageHtml}
                openFileDialog={this.openFileDialog}
                getInputProps={getInputProps}
                getRootProps={getRootProps}
                uploadButtons={this._uploadButtons}
              />
            )
          )}
        </Dropzone>
        <UploadCta>
          <ButtonWithSpinner
            disabled={!this.state.previews.size || this.state.uploading}
            onClick={this.uploadFiles}
            loading={this.state.uploading}
            data-event='submitForReview'
          >
            Submit for Review
          </ButtonWithSpinner>
        </UploadCta>
      </div>
    );
  }

  public readonly closeInvalidTypeModal: () => void = () => {
    this.setState({ showInvalidTypeModal: false });
  }

  public readonly uploadFiles: React.MouseEventHandler<HTMLElement> = async () => {
    this.setState({ uploading: true });
    try {
      const results: UploadFileResponse[] = await Promise.all(
        [...this.state.previews.values()].map(async img =>
          uploadFile(
            img,
            this.props.uploadType,
            '',
          ),
        ),
      );

      this.setState({ uploading: false });

      const anyFailed = results.some(({ success }) => !success);
      const successful: Array<WithFileName<IUploadFileSuccessResponse>> = results.filter(wasSuccessfulUpload);

      successful.forEach(({ fileName }) => {
        this.removeImage(fileName);
      });

      const withRefreshToken: WithFileName<IUploadFileSuccessResponse> | undefined = successful.find(({ refreshToken }) => !!refreshToken);

      if (withRefreshToken) {
        SessionManager.setToken(withRefreshToken.refreshToken);
      }

      if (anyFailed) {
        throw new Error('Upload Failure');
      }

      if (this.props.onSuccess) {
        this.props.onSuccess();
      }
    } catch (e) {
      this.setState({
        uploading: false,
        showUploadErrorModal: true,
        showInvalidTypeModal: false,
      });
    }
  }

  public readonly handleDrop = async (files: IFileWithPreview[]) => {
    this.addImages(files);
  }

  public readonly handleDropFailure = () => {
    this.setState({ showUploadErrorModal: false, showInvalidTypeModal: true });
  }

  public readonly closeUploadErrorModal: () => void = () => {
    this.setState({ showUploadErrorModal: false });
  }

  public readonly removeImageHtml: React.MouseEventHandler<HTMLElement> = ({
    currentTarget: {
      dataset: { name },
    },
  }) => {
    if (name) {
      this.removeImage(name);
    }
  }

  public readonly openFileDialog: React.MouseEventHandler<HTMLElement> = e => {
    if (this._dropzone && this._dropzone.current) {
      this._dropzone.current.open();
    }
    e.preventDefault();
    e.stopPropagation();
  }

  public readonly addImages: (images: IFileWithPreview[]) => void = images => {
    this.setState(({ previews }) => {
      const clone: Map<string, IFileWithPreview> = new Map([
        ...previews,
        ...images.map(image => {
          image.preview = URL.createObjectURL(image);

          return [image.name, image] as [string, IFileWithPreview];
        }),
      ]);

      return { previews: clone };
    });
  }

  public readonly removeImage: (name: string) => void = name => {
    this.setState(({ previews }) => {
      const file: IFileWithPreview | undefined = previews.get(name);
      if (!file) {
        console.error(`tried to remove ${name}, but does not exist`);

        return { previews };
      }
      if (file.preview) {
        window.URL.revokeObjectURL(file.preview);
      }
      const clone: Map<string, IFileWithPreview> = new Map(previews);
      clone.delete(name);

      return { previews: clone };
    });
  }
}

export default DropForCustomer;
